/**
 * Pure numeric helpers shared by simulation code.
 *
 * Everything here is deterministic and side-effect free, see the hard rules in src/AGENTS.md.
 */

/** Constrains `value` to the inclusive range [`min`, `max`]. */
export function clamp(value: number, min: number, max: number): number {
  if (min > max) {
    throw new RangeError(`clamp: min (${String(min)}) must not exceed max (${String(max)})`);
  }
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

/** Linearly interpolates from `a` to `b` by `t`, with `t` clamped to [0, 1]. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * clamp(t, 0, 1);
}

const DEGREES_PER_RADIAN = 180 / Math.PI;

/** Converts degrees to radians. */
export function degToRad(degrees: number): number {
  return degrees / DEGREES_PER_RADIAN;
}

/** Converts radians to degrees. */
export function radToDeg(radians: number): number {
  return radians * DEGREES_PER_RADIAN;
}

/** Converts minutes of arc to radians. Weapon spread is authored in MOA. */
export function moaToRad(moa: number): number {
  return degToRad(moa / 60);
}

/** Wraps an angle into (-pi, pi]. */
export function wrapAngle(radians: number): number {
  const wrapped = ((radians + Math.PI) % (2 * Math.PI)) - Math.PI;
  return wrapped <= -Math.PI ? wrapped + 2 * Math.PI : wrapped;
}

/** Shortest signed difference from `from` to `to`, in (-pi, pi]. */
export function angleDelta(from: number, to: number): number {
  return wrapAngle(to - from);
}

/** Moves `current` toward `target` by at most `maxDelta`, never overshooting. */
export function approach(current: number, target: number, maxDelta: number): number {
  if (maxDelta < 0) {
    throw new RangeError(`approach: maxDelta (${String(maxDelta)}) must not be negative`);
  }
  const difference = target - current;
  if (Math.abs(difference) <= maxDelta) return target;
  return current + Math.sign(difference) * maxDelta;
}
